import { useCallback, useMemo } from 'react';

import { useNotificationBadgeQuery } from 'gqlhooks';

import { usePageVisiblePoll } from 'core/hooks/Meta/usePageVisiblePoll';

export interface ISoundAlarm {
  active: boolean;
  taskId?: string | null;
  title?: string | null;
}

export interface IUseNotificationBadge {
  unreadCount: number;
  totalCount: number;
  latestId: string | null;
  soundAlarm: ISoundAlarm;
  pollSeconds: number;
  loading: boolean;
  refetch: () => Promise<unknown>;
}

const QUIET: ISoundAlarm = { active: false, taskId: null, title: null };

/**
 * Счётчик колокольчика (ТЗ 0068).
 *
 * Опрос идёт по канону ТЗ 0008 — через `usePageVisiblePoll`, то есть в
 * скрытой вкладке он ОСТАНАВЛИВАЕТСЯ. Свёрнутая ERP не должна долбить
 * однопоточный бэкенд.
 *
 * Темп задаёт СЕРВЕР (`pollSeconds`), а не константа во фронте: если станет
 * тесно, интервал поднимается настройкой без релиза.
 *
 * Никакой клиентской арифметики здесь нет. Число, повод для звука и лента
 * считаются на сервере одной функцией — вторая, местная копия правила
 * «высокий приоритет на сегодня» неизбежно разошлась бы с первой.
 */
export const useNotificationBadge = (): IUseNotificationBadge => {
  const {
    data,
    loading,
    refetch,
    startPolling,
    stopPolling,
  } = useNotificationBadgeQuery({ fetchPolicy: 'cache-and-network' });

  const summary = data?.staffNotificationsSummary;
  const pollSeconds = summary?.pollSeconds ?? 60;

  usePageVisiblePoll(startPolling, stopPolling, pollSeconds * 1000);

  const soundAlarm = useMemo<ISoundAlarm>(
    () =>
      summary?.soundAlarm
        ? {
            active: Boolean(summary.soundAlarm.active),
            taskId: summary.soundAlarm.taskId ?? null,
            title: summary.soundAlarm.title ?? null,
          }
        : QUIET,
    [summary?.soundAlarm],
  );

  const refresh = useCallback(() => refetch(), [refetch]);

  return {
    unreadCount: summary?.unreadCount ?? 0,
    totalCount: summary?.totalCount ?? 0,
    latestId: summary?.latestId ?? null,
    soundAlarm,
    pollSeconds,
    loading,
    refetch: refresh,
  };
};
